import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ActivityRowDetail.scss';
import { BuyerCard, ListingCard } from '../ListingCard';
import type { ListingCardListing } from '../ListingCard';
import ListingCardManageMenu from '../ListingCard/ListingCardManageMenu';
import { useListingManagement } from '../ListingCard/useListingManagement';
import { shareOffMarketWillingness } from '../../utils/shareUtils';
import { openBuildingInNewTab } from '../utils/buildingLink';
import { useOpenListing } from '../utils/useOpenListing';
import BoostModal from '../common/BoostModal';
import { apiRequest } from '../utils/api';

import {
  fetchActivityMatches,
  markActivitySeen,
} from '../ActivityView/activityApi';
import { buyingMatchSubtitle } from '../ActivityView/buyingMatchSubtitle';
import { unconfirmedDetailsLabel } from '../ExploreListings/matchDetails';
import type {
  ActivityMatches,
  ActivitySection,
} from '../ActivityView/activityApi';
import ActivityRowVisual from '../ActivityView/ActivityRowVisual';
import type { RowVariant } from '../ActivityView/ActivityRowVisual';
import { deleteAreaInterest } from '../AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';
import type { AreaInterest } from '../AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';

const JAAKKO_AVATAR = `${process.env.PUBLIC_URL}/assets/jaakko_avatar.png`;

export type ActivityRowDetailRow = {
  section: ActivitySection;
  entityId: string;
  variant: RowVariant;
  title: string;
  subtitle: string;
  count?: number;
  image?: string | null;
};

type ActivityRowDetailProps = {
  row: ActivityRowDetailRow;
  onBack: () => void;
  /** True while the slide-out is running, just before the parent unmounts us. */
  closing?: boolean;
  isLoggedIn?: boolean;
  setLoginModalVisible?: (visible: boolean) => void;
};

// A rightward drag past this many pixels counts as "back" on release; shorter
// drags spring back to place.
const SWIPE_BACK_PX = 80;

/** Back chevron in the detail header. */
const BackGlyph = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M15 5L8 12L15 19"
      stroke="#5b6970"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Overflow glyph for the header manage menu (Figma 3845:95862). */
const OverflowGlyph = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M7.33268 11.0003C7.33268 12.0128 6.51187 12.8337 5.49935 12.8337C4.48683 12.8337 3.66602 12.0128 3.66602 11.0003C3.66602 9.9878 4.48683 9.16699 5.49935 9.16699C6.51187 9.16699 7.33268 9.9878 7.33268 11.0003Z"
      fill="#29363D"
    />
    <path
      d="M13.291 11.0003C13.291 12.0128 12.4702 12.8337 11.4577 12.8337C10.4452 12.8337 9.62435 12.0128 9.62435 11.0003C9.62435 9.9878 10.4452 9.16699 11.4577 9.16699C12.4702 9.16699 13.291 9.9878 13.291 11.0003Z"
      fill="#29363D"
    />
    <path
      d="M19.2493 11.0003C19.2493 12.0128 18.4285 12.8337 17.416 12.8337C16.4035 12.8337 15.5827 12.0128 15.5827 11.0003C15.5827 9.9878 16.4035 9.16699 17.416 9.16699C18.4285 9.16699 19.2493 9.9878 19.2493 11.0003Z"
      fill="#29363D"
    />
  </svg>
);

// Once the user closes the boost promo or follows its CTA, it stays gone. Local
// storage rather than component state: the row detail remounts on every open,
// which is what made the promo keep coming back.
const BOOST_PROMO_STORAGE_KEY = 'jaakko_activity_boost_promo_dismissed';

/** Dismiss cross on the boost promo. */
const CloseGlyph = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3 3L13 13M13 3L3 13"
      stroke="#131c20"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Manage menu for an area-interest row. Edit re-opens CouldBuyFlow pre-filled by
 * dispatching 'jaakko:edit-area-interest', the same event ProfileView uses — the
 * flow lives too far up the tree for a prop path — carrying the interest id so
 * the flow PUTs instead of POSTing.
 */
const AreaManageMenu = ({
  interest,
  onDone,
}: {
  interest: AreaInterest;
  onDone: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <ListingCardManageMenu
      icon={<OverflowGlyph />}
      onEdit={() => {
        window.dispatchEvent(
          new CustomEvent('jaakko:edit-area-interest', {
            detail: { interest },
          }),
        );
        onDone();
      }}
      // A failed delete keeps the detail open and says so, the same way the
      // listing manage menu reports one — going back regardless would read as a
      // successful delete of a row that is still there.
      onDelete={async () => {
        try {
          await deleteAreaInterest(interest.area_interest_id);
        } catch (err) {
          console.error('Error deleting area interest:', err);
          alert(t('common.error', 'An error occurred. Please try again.'));
          return;
        }
        window.dispatchEvent(new Event('jaakko:listings-changed'));
        onDone();
      }}
    />
  );
};

/**
 * Share / Edit / Delete menu for a row the user owns (Figma 3850:105866).
 * Split into its own component so useListingManagement is only ever called with
 * a real listing, keeping the hook call unconditional.
 */
const DetailManageMenu = ({
  listing,
  onChanged,
}: {
  listing: ListingCardListing;
  onChanged: () => void;
}) => {
  const { t } = useTranslation();
  const { openEdit, openDelete, modals } = useListingManagement(listing, {
    onChanged,
  });
  // Share goes straight to the OS share sheet. The hook's own openShare opens
  // SharePromptModal first, which exists to upsell a boost right after
  // publishing — a detour when the user picked Share from a manage menu.
  const share = () =>
    shareOffMarketWillingness({
      buildingId: listing.building_id || '',
      eventId: listing._id,
      isSellInterest: !!listing.could_sell,
      address: listing.address,
      t,
    });
  return (
    <>
      <ListingCardManageMenu
        icon={<OverflowGlyph />}
        onEdit={openEdit}
        onDelete={openDelete}
        onShare={share}
        shareDisabledLabel={
          listing.moderation_hidden
            ? t('shareButton.notShareable', 'Not shareable')
            : undefined
        }
      />
      {modals}
    </>
  );
};

/**
 * One Activity row's matches: the buyers or listings the row was reporting.
 * Rendered inside the ActivityView shell, so it inherits the panel's mobile
 * full-screen / desktop centered behaviour rather than opening a second modal.
 */
const ActivityRowDetail = ({
  row,
  onBack,
  closing,
  isLoggedIn,
  setLoginModalVisible,
}: ActivityRowDetailProps) => {
  const { t } = useTranslation();
  const openListing = useOpenListing();
  const [matches, setMatches] = useState<ActivityMatches | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const requestGeneration = useRef(0);
  // Seeded from storage, so dismissing sticks across row opens and reloads
  // instead of resetting every time this component mounts.
  const [boostDismissed, setBoostDismissed] = useState(
    () => localStorage.getItem(BOOST_PROMO_STORAGE_KEY) === 'true',
  );
  const [boostOpen, setBoostOpen] = useState(false);

  /** Retire the promo for good — the user has either closed it or acted on it. */
  const dismissBoostPromo = () => {
    setBoostDismissed(true);
    localStorage.setItem(BOOST_PROMO_STORAGE_KEY, 'true');
  };

  // Kept as a callback so the manage menu can re-run it after an edit or delete
  // changes the row's own listing.
  const load = useCallback(() => {
    const generation = ++requestGeneration.current;
    setIsLoading(true);
    setLoadFailed(false);
    return fetchActivityMatches(row.section, row.entityId)
      .then((page) => {
        if (generation !== requestGeneration.current) return;
        setMatches(page);
        if (row.section === 'buying_interests') {
          return markActivitySeen(row.section, row.entityId, page.match_ids)
            .then(() => {
              window.dispatchEvent(
                new CustomEvent('jaakko:activity-seen', {
                  detail: {
                    section: row.section,
                    entityId: row.entityId,
                    remaining: page.match_ids
                      ? Math.max(
                          0,
                          (page.new_listing_count ?? 0) -
                            page.listings.filter((item) => item.is_new).length,
                        )
                      : 0,
                  },
                }),
              );
            })
            .catch(() => undefined);
        }
      })
      .catch(() => {
        if (generation === requestGeneration.current) setLoadFailed(true);
      })
      .finally(() => {
        if (generation === requestGeneration.current) setIsLoading(false);
      });
  }, [row.section, row.entityId]);

  const loadMore = async () => {
    if (!matches?.next_cursor || isLoadingMore) return;
    const generation = requestGeneration.current;
    setIsLoadingMore(true);
    setLoadFailed(false);
    try {
      const page = await fetchActivityMatches(
        row.section,
        row.entityId,
        matches.next_cursor,
      );
      if (generation !== requestGeneration.current) return;
      setMatches((previous) =>
        previous
          ? {
              ...page,
              listings: Array.from(
                new Map(
                  [...previous.listings, ...page.listings].map((item) => [
                    item._id,
                    item,
                  ]),
                ).values(),
              ),
            }
          : page,
      );
      try {
        await markActivitySeen(row.section, row.entityId, page.match_ids);
      } catch {
        return;
      }
      if (generation === requestGeneration.current)
        window.dispatchEvent(
          new CustomEvent('jaakko:activity-seen', {
            detail: {
              section: row.section,
              entityId: row.entityId,
              remaining: Math.max(
                0,
                (page.new_listing_count ?? 0) -
                  page.listings.filter((item) => item.is_new).length,
              ),
            },
          }),
        );
    } catch {
      if (generation === requestGeneration.current) setLoadFailed(true);
    } finally {
      if (generation === requestGeneration.current) setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
    return () => {
      requestGeneration.current += 1;
    };
  }, [load]);

  // Swipe-to-go-back. The drag is only claimed once the finger has moved further
  // horizontally than vertically and to the right, so a normal vertical scroll
  // through the card list is never hijacked.
  const drag = useRef({ startX: 0, startY: 0, axis: '' as '' | 'x' | 'y' });
  const [dragX, setDragX] = useState(0);

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    drag.current = { startX: t.clientX, startY: t.clientY, axis: '' };
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const t = e.touches[0];
    const dx = t.clientX - drag.current.startX;
    const dy = t.clientY - drag.current.startY;
    if (!drag.current.axis) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      drag.current.axis = Math.abs(dx) > Math.abs(dy) && dx > 0 ? 'x' : 'y';
    }
    if (drag.current.axis === 'x') setDragX(Math.max(0, dx));
  };

  const onTouchEnd = () => {
    if (drag.current.axis === 'x' && dragX > SWIPE_BACK_PX) onBack();
    drag.current.axis = '';
    setDragX(0);
  };

  // The boost request notifies the team (Slack + email); it charges nothing on
  // its own. The user confirms it inside BoostModal. Area-interest rows have no
  // backing listing, so they identify themselves by the row's own entity id.
  const handleBoostConfirm = async () => {
    await apiRequest('/boost-listing', {
      method: 'POST',
      body: {
        eventId: subject?._id ?? row.entityId,
        buildingId: subject?.building_id ?? null,
        actionType: row.section === 'my_places' ? 'sell' : 'buy',
        buildingAddress: row.title,
      },
    });
  };

  /**
   * "Could buy" on a matched seller listing: open a draft chat with that
   * seller, the same path Explore's cards, the building view and the listing
   * page take. The buyer viewing this detail already holds the buying interest
   * that surfaced the listing, so re-opening the could-buy wizard here (the old
   * behavior) only dead-ended back into creating a duplicate interest; contact
   * is the remaining action. Nothing is created by the click itself: the
   * conversation materializes from the buyer's first message
   * (POST /conversations/could-buy/start). Only signed-in viewers reach this:
   * the Activity view renders with the bottom nav, which needs a login. The
   * detail stays open underneath so closing the chat returns here, ready for
   * the next listing.
   */
  const onCouldBuyListing = (item: ListingCardListing) => {
    const street = [item.address?.street, item.address?.houseNumber]
      .filter(Boolean)
      .join(' ');
    const title = [street, item.address?.city].filter(Boolean).join(', ');
    window.dispatchEvent(
      new CustomEvent('jaakko:open-draft-chat', {
        detail: { sellWillingnessId: item._id, title },
      }),
    );
  };

  /**
   * Split one match list into the new entries and the rest. The endpoint returns
   * newest first, so the row's own counter tells us how many lead the list.
   *
   * Both groups get the same divider when both exist, so the leading cards read
   * as "new" rather than as an unexplained first group. An all-seen list stays
   * unlabelled: with nothing new to contrast against, a lone header is noise.
   */
  const renderList = (
    items: ListingCardListing[],
    newLabel: string,
    otherLabel: string,
    renderCard: (item: ListingCardListing) => React.ReactNode,
  ) => {
    if (items.length === 0) return null;
    const newCount = Math.min(row.count ?? 0, items.length);
    const hasDiscoveryState = items.some((item) => item.is_new !== undefined);
    const fresh = hasDiscoveryState
      ? items.filter((item) => item.is_new)
      : items.slice(0, newCount);
    const rest = hasDiscoveryState
      ? items.filter((item) => !item.is_new)
      : items.slice(newCount);
    const showDividers = fresh.length > 0 && rest.length > 0;
    const divider = (label: string) => (
      <div className="activity-detail__divider">
        <span className="activity-detail__divider-label">{label}</span>
      </div>
    );
    return (
      <>
        {showDividers && divider(newLabel)}
        {fresh.map(renderCard)}
        {showDividers && divider(otherLabel)}
        {rest.map(renderCard)}
      </>
    );
  };

  const buyers = matches?.buyers ?? [];
  const listings = matches?.listings ?? [];
  const isEmpty =
    !isLoading && !loadFailed && buyers.length === 0 && listings.length === 0;
  const confirmedListings = listings.filter(
    (item) => item.match_status !== 'unconfirmed',
  );
  const possibleListings = listings.filter(
    (item) => item.match_status === 'unconfirmed',
  );
  const renderSeller = (item: ListingCardListing) => {
    const card = (
      <ListingCard
        listing={item}
        size="big"
        onOpen={openListing}
        isLoggedIn={isLoggedIn}
        setLoginModalVisible={setLoginModalVisible}
        onToggleCouldBuy={() => onCouldBuyListing(item)}
      />
    );
    return item.match_status === 'unconfirmed' ? (
      <div key={item._id} className="activity-detail__possible-card">
        {card}
        <p className="activity-detail__possible-note">
          {unconfirmedDetailsLabel(
            item.unconfirmed_fields,
            (key) =>
              t(
                key === 'exploreListings.unconfirmedField.seller.floor'
                  ? 'newLandingPage.activity.floorUnconfirmed'
                  : key,
              ),
            'seller',
          )}
        </p>
      </div>
    ) : (
      <React.Fragment key={item._id}>{card}</React.Fragment>
    );
  };
  const subject = matches?.subject ?? null;
  const areaInterest = matches?.area_interest ?? null;
  const subtitle =
    matches?.confirmed_listing_count !== undefined
      ? buyingMatchSubtitle(matches, t)
      : row.subtitle;
  // My-places and Following rows are keyed by building id already; a buying
  // interest is keyed by its own id, so its building comes from the listing it
  // backs — or, for an area interest, from the anchor it was drawn around. An
  // area interest with no anchor has no building page to open.
  const buildingId =
    row.section === 'buying_interests'
      ? (subject?.building_id ?? areaInterest?.anchor_building_id ?? null)
      : row.entityId;
  // The promo advertises boosting the user's own listing, so it runs on both
  // sides they own — my places (sell) and buying interests (buy) — but not on
  // Following, where the building isn't theirs. Area interests have no backing
  // listing doc, so this is keyed on the section rather than on `subject`.
  const isSellSide = row.section === 'my_places';
  // Eligibility and promo visibility are separate: the CTA dismisses the promo,
  // so gating the modal on `showBoost` too would unmount it on the same click
  // that opens it.
  const isBoostEligible = isSellSide || row.section === 'buying_interests';
  const showBoost = !boostDismissed && isBoostEligible;

  return (
    <div
      className={`activity-detail${closing ? ' activity-detail--closing' : ''}${
        dragX > 0 ? ' activity-detail--dragging' : ''
      }`}
      style={dragX > 0 ? { transform: `translateX(${dragX}px)` } : undefined}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <div className="activity-detail__header">
        <button
          type="button"
          className="activity-detail__back"
          onClick={onBack}
          aria-label={t('newLandingPage.activity.detail.back')}
        >
          <BackGlyph />
        </button>
        <button
          type="button"
          className="activity-detail__identity"
          onClick={() => buildingId && openBuildingInNewTab(buildingId)}
          disabled={!buildingId}
        >
          <ActivityRowVisual
            variant={row.variant}
            image={row.image}
            className="activity-detail__visual"
          />
          <div className="activity-detail__heading">
            <p className="activity-detail__title">{row.title}</p>
            {subtitle && (
              <p className="activity-detail__subtitle">{subtitle}</p>
            )}
          </div>
        </button>
        {subject && (
          <div className="activity-detail__manage">
            <DetailManageMenu listing={subject} onChanged={load} />
          </div>
        )}
        {!subject && areaInterest && (
          <div className="activity-detail__manage">
            <AreaManageMenu interest={areaInterest} onDone={onBack} />
          </div>
        )}
      </div>

      {showBoost && (
        <div className="activity-detail__boost">
          <img
            className="activity-detail__boost-avatar"
            src={JAAKKO_AVATAR}
            alt=""
            aria-hidden="true"
          />
          <div className="activity-detail__boost-text">
            <p className="activity-detail__boost-title">
              {t('newLandingPage.activity.detail.boost.title')}
            </p>
            <p className="activity-detail__boost-body">
              {t(
                isSellSide
                  ? 'newLandingPage.activity.detail.boost.bodySell'
                  : 'newLandingPage.activity.detail.boost.bodyBuy',
              )}{' '}
              <button
                type="button"
                className="activity-detail__boost-link"
                onClick={() => {
                  dismissBoostPromo();
                  setBoostOpen(true);
                }}
              >
                {t('newLandingPage.activity.detail.boost.cta')}
              </button>
            </p>
          </div>
          <button
            type="button"
            className="activity-detail__boost-close"
            onClick={dismissBoostPromo}
            aria-label={t('newLandingPage.activity.detail.boost.dismiss')}
          >
            <CloseGlyph />
          </button>
        </div>
      )}

      <div className="activity-detail__body">
        {isLoading && (
          <div className="activity-detail__shimmer" aria-hidden="true" />
        )}
        {isEmpty && (
          <p className="activity-detail__empty">
            {t('newLandingPage.activity.detail.empty')}
          </p>
        )}
        {renderList(
          confirmedListings,
          t('newLandingPage.activity.detail.newListings'),
          t('newLandingPage.activity.detail.otherListings'),
          renderSeller,
        )}
        {possibleListings.length > 0 && (
          <>
            <div className="activity-detail__possible-heading">
              <strong>{t('newLandingPage.activity.detail.possibleTitle')}</strong>
              <p>{t('newLandingPage.activity.detail.possibleDescription')}</p>
            </div>
            {renderList(
              possibleListings,
              t('newLandingPage.activity.detail.newListings'),
              t('newLandingPage.activity.detail.otherListings'),
              renderSeller,
            )}
          </>
        )}
        {loadFailed && <p role="alert">{t('common.error')}</p>}
        {matches?.next_cursor && (
          <button
            type="button"
            className="activity-detail__load-more"
            disabled={isLoadingMore}
            onClick={loadMore}
          >
            {t('newLandingPage.activity.loadMore')}
          </button>
        )}
        {loadFailed && !matches?.next_cursor && (
          <button type="button" onClick={load}>
            {t('newLandingPage.activity.retry')}
          </button>
        )}
        {renderList(
          buyers,
          t('newLandingPage.activity.detail.newBuyers'),
          t('newLandingPage.activity.detail.otherBuyers'),
          (item) => (
            <BuyerCard
              key={item._id}
              listing={item}
              size="big"
              onOpen={openListing}
              isLoggedIn={isLoggedIn}
              setLoginModalVisible={setLoginModalVisible}
            />
          ),
        )}
      </div>

      {isBoostEligible && (
        <BoostModal
          isOpen={boostOpen}
          onClose={() => setBoostOpen(false)}
          onConfirm={handleBoostConfirm}
          actionType={isSellSide ? 'sell' : 'buy'}
          buildingAddress={row.title}
        />
      )}
    </div>
  );
};

export default ActivityRowDetail;
